import { variables } from '$lib/env';
import { AuthenticationService } from '$lib/services/authentication-service';
import {
	CustomDataSource,
	CzmlDataSource,
	HeightReference,
	HorizontalOrigin,
	RequestErrorEvent,
	Resource,
	VerticalOrigin
} from 'cesium';
import { get } from 'svelte/store';
import { mapModeStore } from './map-mode-store';
import { toast } from '$lib/components/organisms/toast/toast';

let operationalMarkingDatasource: CustomDataSource;
let rectangleAreaDataSource: CustomDataSource;
let mapdataDataSource: CzmlDataSource;
let measureDistancePolylineDataSource: CzmlDataSource;
let distanceAnalysisPolylinesDataSource: CzmlDataSource; // Used for storing polylines and labels in distance analysis
let distanceAnalysisTerrainProfilesDataSource: CzmlDataSource; // Used for storing terrain profiles in distance analysis
let temporaryConfiguredLinesDataSource: CzmlDataSource; // Used for visualizing lines before updating or creating them.
let sandbox_polygonDataSource: CustomDataSource;
let sandbox_PolylineDataSource: CustomDataSource;
let sandbox_LabelDataSource: CzmlDataSource;
let sandbox_RectangleDataSource: CzmlDataSource;
let sandbox_MarkerDataSource: CzmlDataSource;
let sandbox_BoxDataSource: CzmlDataSource; // Used for displaying drawing-tool 3D-boxes
let terrainProfilePolylineDataSource: CzmlDataSource; // Used for showing terrain profile which can be exported to NIS
let temp_referenceDataSource: CzmlDataSource; // Used for showing temporary entities in an isolated context (e.g previewing while using a tool, showing the live version of a plan-object, etc.)
let aviationObstacleDataSource: CzmlDataSource; // Used for displaying billboards of Aviation obstacles.
let trenchEntityDatasource: CzmlDataSource; // Used for displaying trench entities. Until trenches are save backend.
let smartdataImageDataSource: CustomDataSource; // Billboards for Feltkatalog images inside a drawn area (image-area tool).

const DataSourceKeys = {
	OPERATIONALMARKING_DATASOURCE: 'OPERATIONALMARKING_DATASOURCE',
	RECTANGLE_AREA_DATASOURCE: 'RECTANGLE_AREA_DATASOURCE',
	MASTPOINT_DATASOURCE: 'MASTPOINT_DATASOURCE',
	MEASURE_DISTANCE_POLYLINE_DATASOURCE: 'MEASURE_DISTANCE_POLYLINE_DATASOURCE',
	TERRAIN_PROFILE_POLYLINE_DATASOURCE: 'TERRAIN_PROFILE_POLYLINE_DATASOURCE',
	DISTANCE_ANALYSIS_POLYLINES_DATASOURCE: 'DISTANCE_ANALYSIS_POLYLINES_DATASOURCE',
	TEMPORARY_CONFIGURED_LINES: 'TEMPORARY_CONFIGURED_LINES',
	SANDBOX_SELECTION_POLYGON_DATASOURCE: 'SANDBOX_SELECTION_POLYGON_DATASOURCE',
	SANDBOX_LABEL_DATASOURCE: 'SANDBOX_LABEL_DATASOURCE',
	SANDBOX_POLYLINE_DATASOURCE: 'SANDBOX_POLYLINE_DATASOURCE',
	SANDBOX_RECTANGLE_DATASOURCE: 'SANDBOX_RECTANGLE_DATASOURCE',
	SANDBOX_MARKER_DATASOURCE: 'SANDBOX_MARKER_DATASOURCE',
	SANDBOX_BOX_DATASOURCE: 'SANDBOX_BOX_DATASOURCE',
	TEMPORARY_REFERENCE_DATASOURCE: 'TEMPORARY_REFERENCE_DATASOURCE',
	AVIATION_OBSTACLE_DATASOURCE: 'AVIATION_OBSTACLE_DATASOURCE',
	TERRAIN_PROFILE_EXPORT_DATASOURCE: 'TERRAIN_PROFILE_EXPORT_DATASOURCE',
	TRECNCH_ENTITY_DATASOURCE: 'TRENCH_ENTITY_DATASOURCE',
	SMARTDATA_IMAGE_DATASOURCE: 'SMARTDATA_IMAGE_DATASOURCE'
} as const;

/**
 * Fetching data using Cesium's Resource may fail and hit a catch-block.
 * This function handles if the Resource failed due to 401 (unauthorized, likely an expired user token).
 * @param event
 */
const handleUnauthorizedResourceFetch = async (event: RequestErrorEvent) => {
	if (event.statusCode === 401) {
		toast({
			type: 'error',
			title: 'Brukersesjon utløpt',
			description: `Din brukersesjon har utløpt. Logg inn på nytt for å fortsette å bruke SmartGIS.`
		});
		await AuthenticationService.signOut();
	}
};

// Image clusters get a generated pill (image glyph + exact count) instead of
// the hotspot sprites. One data URL per distinct count, cached — Cesium reuses
// the texture for billboards sharing the same image reference.
const imageClusterIconCache = new Map<number, string>();
const imageClusterIcon = (count: number): string => {
	const cached = imageClusterIconCache.get(count);
	if (cached) return cached;
	const text = String(count);
	const textWidth = 9 * text.length;
	const width = 40 + textWidth;
	const svg =
		`<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="32" viewBox="0 0 ${width} 32">` +
		`<rect x="1" y="1" width="${width - 2}" height="30" rx="15" fill="#594EF2" stroke="white" stroke-width="2"/>` +
		`<g transform="translate(9,8)" fill="none" stroke="white" stroke-width="1.6">` +
		`<rect x="0.8" y="0.8" width="14.4" height="14.4" rx="2"/>` +
		`<circle cx="5.4" cy="5.4" r="1.7"/>` +
		`<path d="M2.2 13l3.4-4 2.8 3 2.2-2.4 3.2 3.4" stroke-linecap="round" stroke-linejoin="round"/>` +
		`</g>` +
		`<text x="${28 + textWidth / 2}" y="21" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="13" font-weight="700" fill="white">${text}</text>` +
		`</svg>`;
	const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
	imageClusterIconCache.set(count, url);
	return url;
};

const configureClusters = (
	datasource: CustomDataSource | CzmlDataSource,
	billboardprefix: string,
	type: string
) => {
	if (type == 'aviationobstacles') {
		mapdataDataSource.clustering.clusterBillboards = true;
		mapdataDataSource.clustering.clusterLabels = false;
		mapdataDataSource.clustering.clusterPoints = false;
	}
	datasource.clustering.pixelRange = 30;
	datasource.clustering.enabled = true;

	datasource.clustering.minimumClusterSize = 3;
	datasource.clustering.clusterEvent.addEventListener((clusteredEntities, cluster) => {
		if (type == 'labels' || type == 'images') {
			cluster.billboard.heightReference = HeightReference.CLAMP_TO_GROUND;
		}
		if (type == 'images') {
			// Cluster billboards never reach `selectedEntityChanged`; carrying the
			// clustered entities as the billboard's pick id lets the image-area
			// tool's own click handler resolve which images were clicked.
			cluster.billboard.id = clusteredEntities;
		}
		cluster.label.show = false;
		cluster.billboard.show = true;
		cluster.billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY;
		cluster.billboard.verticalOrigin = VerticalOrigin.CENTER;
		cluster.billboard.horizontalOrigin = HorizontalOrigin.CENTER;
		cluster.billboard.scale = 1;

		if (type == 'images') {
			cluster.billboard.image = imageClusterIcon(clusteredEntities.length);
			return;
		}

		if (clusteredEntities.length >= 1000) {
			cluster.billboard.image = `/gfx/cluster-billboards/${billboardprefix}hotspot-1000.png`;
		} else if (clusteredEntities.length >= 500) {
			cluster.billboard.image = `/gfx/cluster-billboards/${billboardprefix}hotspot-500.png`;
		} else if (clusteredEntities.length >= 100) {
			cluster.billboard.image = `/gfx/cluster-billboards/${billboardprefix}hotspot-100.png`;
		} else if (clusteredEntities.length >= 50) {
			cluster.billboard.image = `/gfx/cluster-billboards/${billboardprefix}hotspot-50.png`;
		} else if (clusteredEntities.length >= 10) {
			cluster.billboard.image = `/gfx/cluster-billboards/${billboardprefix}hotspot-10.png`;
		} else if (clusteredEntities.length < 10) {
			cluster.billboard.image = `/gfx/cluster-billboards/${billboardprefix}hotspot-${clusteredEntities.length}.png`;
		}
	});
};

const createDataSources = () => {
	operationalMarkingDatasource = new CustomDataSource(DataSourceKeys.OPERATIONALMARKING_DATASOURCE);
	rectangleAreaDataSource = new CustomDataSource(DataSourceKeys.RECTANGLE_AREA_DATASOURCE);
	mapdataDataSource = new CzmlDataSource(DataSourceKeys.MASTPOINT_DATASOURCE);
	distanceAnalysisPolylinesDataSource = new CzmlDataSource(
		DataSourceKeys.DISTANCE_ANALYSIS_POLYLINES_DATASOURCE
	);
	distanceAnalysisTerrainProfilesDataSource = new CzmlDataSource(
		DataSourceKeys.TERRAIN_PROFILE_POLYLINE_DATASOURCE
	);
	measureDistancePolylineDataSource = new CzmlDataSource(
		DataSourceKeys.MEASURE_DISTANCE_POLYLINE_DATASOURCE
	);
	temporaryConfiguredLinesDataSource = new CzmlDataSource(
		DataSourceKeys.TEMPORARY_CONFIGURED_LINES
	);
	sandbox_polygonDataSource = new CustomDataSource(
		DataSourceKeys.SANDBOX_SELECTION_POLYGON_DATASOURCE
	);
	sandbox_PolylineDataSource = new CzmlDataSource(DataSourceKeys.SANDBOX_POLYLINE_DATASOURCE);
	sandbox_LabelDataSource = new CzmlDataSource(DataSourceKeys.SANDBOX_LABEL_DATASOURCE);
	sandbox_RectangleDataSource = new CzmlDataSource(DataSourceKeys.SANDBOX_RECTANGLE_DATASOURCE);
	sandbox_MarkerDataSource = new CzmlDataSource(DataSourceKeys.SANDBOX_MARKER_DATASOURCE);
	sandbox_BoxDataSource = new CzmlDataSource(DataSourceKeys.SANDBOX_BOX_DATASOURCE);
	temp_referenceDataSource = new CzmlDataSource(DataSourceKeys.TEMPORARY_REFERENCE_DATASOURCE);
	aviationObstacleDataSource = new CzmlDataSource(DataSourceKeys.AVIATION_OBSTACLE_DATASOURCE);
	terrainProfilePolylineDataSource = new CzmlDataSource(DataSourceKeys.TERRAIN_PROFILE_EXPORT_DATASOURCE);
	trenchEntityDatasource = new CzmlDataSource(DataSourceKeys.TRECNCH_ENTITY_DATASOURCE);
	smartdataImageDataSource = new CustomDataSource(DataSourceKeys.SMARTDATA_IMAGE_DATASOURCE);

	configureClusters(operationalMarkingDatasource, '', 'labels');
	configureClusters(aviationObstacleDataSource, 'ao-', 'aviationobstacles');
	configureClusters(smartdataImageDataSource, '', 'images');

	return useDataSources();
};

const useDataSources = () => {
	return {
		operationalMarkingDatasource,
		sandbox_polygonDataSource,
		mapdataDataSource,
		measureDistancePolylineDataSource,
		distanceAnalysisPolylinesDataSource,
		distanceAnalysisTerrainProfilesDataSource,
		terrainProfilePolylineDataSource,
		temporaryConfiguredLinesDataSource,
		sandbox_LabelDataSource,
		sandbox_RectangleDataSource,
		sandbox_PolylineDataSource,
		sandbox_MarkerDataSource,
		sandbox_BoxDataSource,
		temp_referenceDataSource,
		rectangleAreaDataSource,
		aviationObstacleDataSource,
		trenchEntityDatasource,
		smartdataImageDataSource
	};
};

export {
	createDataSources,
	useDataSources,
	handleUnauthorizedResourceFetch
};
